import { useEffect, useId, useState } from 'react';
import {
  MAX_NAME_LENGTH,
  checkName,
  nameRejectionMessage,
} from '../../../shared/playerName.ts';

export interface NameEditorProps {
  readonly currentName: string;
  readonly onRename: (name: string) => void;
}

/**
 * Your display name, always visible and always editable.
 *
 * Deliberately not collapsed behind a "Change name" button any more. This sits
 * above the decision to host or join, and it is the label everyone else in the
 * room reads: a queue of `Brave Otter 41` chips tells a host nothing about who
 * they are about to play.
 *
 * Saves on blur as well as on Enter, because a host who types a name and then
 * clicks straight through to Host has plainly finished editing. Validation is
 * the shared `checkName`, so the message shown here is the rule the server will
 * apply.
 */
export function NameEditor({ currentName, onRename }: NameEditorProps) {
  const [draft, setDraft] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  // A name can also change from outside this input — the server trims what was
  // sent, or another tab renames the same account. The field follows it rather
  // than showing a name nobody else can see.
  useEffect(() => {
    setDraft(currentName);
    setError(null);
  }, [currentName]);

  const commit = (): void => {
    const checked = checkName(draft);
    if (!checked.ok) {
      setError(nameRejectionMessage(checked.reason));
      return;
    }
    setError(null);
    setDraft(checked.name);
    // Nothing changed, so there is nothing to announce or persist.
    if (checked.name === currentName) return;
    onRename(checked.name);
  };

  return (
    <form
      className="nameeditor"
      onSubmit={(event) => {
        event.preventDefault();
        commit();
      }}
    >
      <label className="nameeditor__label" htmlFor={inputId}>
        Your name
      </label>
      <div className="nameeditor__row">
        <input
          id={inputId}
          className="nameeditor__input"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
          onBlur={commit}
          maxLength={MAX_NAME_LENGTH}
          autoComplete="nickname"
          aria-invalid={error !== null}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
        <button type="submit" className="button button--quiet">
          Save
        </button>
      </div>
      {error && (
        <p className="nameeditor__error" id={`${inputId}-error`} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
